import {
  Component,
  ElementRef,
  ExistingProvider,
  forwardRef,
  HostListener,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {distinctUntilChanged} from 'rxjs';
import {IDropdownObject} from 'src/app/interfaces/dropdown-object.interface';

const DROPDOWN: ExistingProvider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DropdownComponent),
  multi: true,
};

@UntilDestroy()
/**
 * The component allows to use custom select of items or multiselect
 */
@Component({
  selector: 'app-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss'],
  providers: [DROPDOWN],
})
export class DropdownComponent implements OnInit {
  /**
   * isMultiselect-choice between common select and multiselect
   */
  @Input() public isMultiselect = false;

  /**
   * hasFilter shows/hides filter icon
   */
  @Input() public hasFilter = false;

  /**
   * isShowFilter shows/hides filter
   */
  @Input() public isShowFilter = false;

  /**
   * isIconSort shows/hides sort icon
   */
  @Input() public isIconSort = false;

  /**
   * countVisibleItems-how many options needs to be shown
   */
  @Input() public countVisibleItems!: number;

  /**
   * hasAllExept shows/hides "All Except" checkbox
   */
  @Input() public hasAllExept = false;

  /**
   * border-can be selected dropdown styles:'none'- background transparent;'blue'-blue border
   */
  @Input() public border = 'classic';

  /**
   * disabl-disable/enable dropdown
   */
  @Input() public disabl = false;

  /**
   * isValueInvisible-dropdown value becomes visible/invisible
   */
  @Input() public isValueInvisible = false;

  /**
   * if isEmitObj = false dropdown emits only string/[]string
   */
  @Input() public emitObj = true;

  @Input() public isEditMode: boolean = true;

  /**
   * items-options for dropdown
   */
  @Input() public set items(value: any) {
    this.itemsProcessing(value);
    this.setOptionsControls();
    this.setCountVisibleItems();
    if (this.lastWrittenValue) this.writeValue(this.lastWrittenValue);
  }
  @Input() isDaggerDropdown = false;

  /**
   * max height of non-scroll options container in pixels
   */
  @Input() displayedOptionsHeight = 300;

  public visibleContent = false; // show/hide options of dropdown
  public radioRandom: number = Math.random(); // random number for radiobutton
  public myForm!: UntypedFormGroup; // form consist of formArray,checkedItemsControl and radioControl
  public formArray!: UntypedFormArray; // for multiselect, formArray consists of controls with values that equel checked checkbox values
  public checkedItemsControl!: UntypedFormControl; // controls with multiselect value
  public radioControl!: UntypedFormControl; // controls with plain select value
  public countItems!: number; // the count of displayed items
  public isItemsArrStr!: boolean; // shows what kind of data has items variable :array of strings or array of objects
  public correctObjItems: IDropdownObject[] = []; // if items is array of objects, correctObjItems saves this array with necessary keys:id and name
  public stringItems: string[] = []; // init names from correctObjItems
  public filteredItems: string[] = []; // filtered names
  public multiSelectValue = ''; // value that displayed in the template if multiselect
  public lastWrittenValue: any; // saves last called value in the writeValue
  public isEmitObj = true; // defines format  of the dropdown value
  public isDropdownOpened = false;
  @ViewChild('dropdown') dropdown!: ElementRef; // main wraper of this component

  /**
   * Dropdown options are closed if click outside the dropdown
   * @param event click
   */
  @HostListener('document:click', ['$event'])
  clickOut(event: MouseEvent) {
    if (this.visibleContent)
      this.visibleContent = !!(
        this.dropdown.nativeElement.contains(event.target) ||
        (event.target as HTMLElement).classList.contains('options__all')
      );
    if (this.visibleContent) this.isDropdownOpened = true;
    if (!this.visibleContent) this.setCountVisibleItems();
    if (!this.visibleContent && this.isDropdownOpened) this.onTouched();
  }

  onChange: any = () => {
    // function must be empty
  }

  onTouched: any = () => {
    //
  }

  ngOnInit(): void {
    this.setCountVisibleItems();
    this.myForm = new UntypedFormGroup({
      items: new UntypedFormArray([]),
      checkedItems: new UntypedFormControl([]),
      radio: new UntypedFormControl(this.filteredItems[0]),
      allExept: new UntypedFormControl(false),
    });
    this.formArray = this.myForm.get('items') as UntypedFormArray;
    this.checkedItemsControl = this.myForm.get(
      'checkedItems'
    ) as UntypedFormControl;
    this.radioControl = this.myForm.get('radio') as UntypedFormControl;
    this.setOptionsControls();
    if (this.isMultiselect || this.isDaggerDropdown) {
      this.checkedItemsControl.valueChanges
        .pipe(untilDestroyed(this), distinctUntilChanged())
        .subscribe(val => {
          if (this.onChange)
            this.onChange(this.isEmitObj ? this.arrStrToArrObj(val) : val);
        });
    } else {
      this.radioControl.valueChanges
        .pipe(untilDestroyed(this), distinctUntilChanged())
        .subscribe(val => {
          if (this.onChange)
            this.onChange(this.isEmitObj ? this.strToObj(val) : val);
          this.visibleContent = false;
          this.setCountVisibleItems();
        });
    }
  }

  /**
   * The method sets the value of the dropdown for multiselect
   */
  patchCheckedItems(): void {
    this.multiSelectValue = this.formArray.value.join(', ');
    this.myForm.patchValue({
      checkedItems: this.formArray.value,
    });
  }

  /**
   * The method processes click on checkbox in the multiselect section
   * @param event event - click on checkbox in the multiselect
   */
  onCheckChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.checked) {
      this.formArray.push(new UntypedFormControl(target.value));
      this.patchCheckedItems();
    } else {
      for (let i = 0; i < this.formArray.controls.length; i++) {
        if (this.formArray.controls[i].value === target.value) {
          this.formArray.removeAt(i);
          this.patchCheckedItems();
          return;
        }
      }
    }
  }

  /**
   * The method filtering checkbox items
   * @param event - keyup event for filter field that place in checkbox items list
   */
  filterItems(event: Event) {
    const value = (event.target as HTMLInputElement).value.toLowerCase();

    this.filteredItems = this.stringItems.filter(item =>
      item.toLowerCase().includes(value)
    );
  }

  removeItem(index: number, item: string) {
    this.formArray.removeAt(index);
    this.myForm.controls[item].setValue(false);
    this.patchCheckedItems();
  }
  /**
   * The method changes input value from array of the checked checkboxes on array of the unchecked chekboxes
   */
  changeAllExept(): void {
    const res = [...this.filteredItems];
    for (const it of this.checkedItemsControl.value) {
      for (let i = 0; i < res.length; i++) {
        if (it === res[i]) {
          this.myForm.controls[res[i]].setValue(false);
          res.splice(i, 1);
        }
      }
    }
    this.formArray.clear();
    for (const dep of res) {
      this.myForm.controls[dep].setValue(true);
      this.formArray.push(new UntypedFormControl(dep));
    }
    this.patchCheckedItems();
  }

  /**
   * The method shows/hides items and sets default value of countItems
   */
  showItems(event: Event): void {
    if (
      !(event.target as HTMLElement).classList.contains(
        'dropdown__autocomplete'
      ) &&
      this.isDaggerDropdown
    )
      return;
    if (!this.disabl) {
      if (this.visibleContent) {
        this.setCountVisibleItems();
        this.isDropdownOpened = true;
      }
      this.visibleContent = !this.visibleContent;
    }
  }

  /**
   * The method sets the value of the dropdown from form control
   * @param value a value that comes from outside and sets the value of the dropdown
   */
  writeValue(value: any): void {
    this.lastWrittenValue = value;
    if (!this.isMultiselect)
      this.radioControl.setValue(
        this.isItemsArrStr || value === null ? value : value?.name
      );
    if ((this.isMultiselect || this.isDaggerDropdown) && Array.isArray(value)) {
      if (!this.isItemsArrStr) value = this.arrObjToArrStr(value);
      this.formArray.clear();
      for (const it of this.filteredItems) {
        if (value.includes(it)) {
          this.myForm.controls[it].setValue(true);
          this.formArray.push(new UntypedFormControl(it));
        } else this.myForm.controls[it].setValue(false);
      }
      this.patchCheckedItems();
    }
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  /**
   * this method: 1.defines type of items; 2.defines what kind of data dropdown will be emit;
   * 3.saves objects from items array with necessary keys(id,name) into correctObjItems ;
   * 4.sets stringItems as array of stings(names).
   * @param itemsValue items from outside
   */
  itemsProcessing(itemsValue: any): void {
    if (!itemsValue) return;
    this.isItemsArrStr = typeof itemsValue[0] !== 'object';
    this.isEmitObj = this.emitObj && !this.isItemsArrStr;
    if (!this.isItemsArrStr) {
      this.correctObjItems = [];
      for (const obj of itemsValue) {
        this.correctObjItems.push({id: obj.id, name: obj.name});
      }
      this.stringItems = this.arrObjToArrStr(this.correctObjItems);
    } else this.stringItems = [...itemsValue];

    this.filteredItems = this.stringItems;
  }

  /**
   * this method converts array of objects to array of strings
   * @param arrObj array of objects
   * @returns array of strings
   */
  arrObjToArrStr(arrObj: IDropdownObject[]): string[] {
    const res = [];
    for (const obj of arrObj) {
      res.push(obj.name);
    }
    return res;
  }

  /**
   * this method converts array of strings to array of objects for emiting from dropdown if it is multiselect
   * @param arrStr array of strings
   * @returns array of objects
   */
  arrStrToArrObj(arrStr: string[]): IDropdownObject[] {
    const res = [];
    for (const obj of this.correctObjItems) {
      if (arrStr.includes(obj.name)) res.push(obj);
    }
    return res;
  }

  /**
   * this method converts string to object for emiting from dropdown if it isn't multiselect
   * @param str string
   * @returns object that has name key equal str
   */
  strToObj(str: string): IDropdownObject | null {
    for (const obj of this.correctObjItems) {
      if (obj.name === str) return obj;
    }
    return null;
  }

  /**
   * this method set controls for all items
   */
  setOptionsControls() {
    for (const item of this.filteredItems) {
      if (this.myForm)
        this.myForm.controls[item]
          ? this.myForm.controls[item].setValue(false)
          : this.myForm.addControl(item, new UntypedFormControl(false));
    }
  }

  /**
   * this method sets count of visible items in dropdown
   */
  setCountVisibleItems() {
    this.countItems = this.countVisibleItems
      ? this.countVisibleItems
      : this.filteredItems.length;
  }
}
